import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Select,
	Stack,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import type { PriceBookItem, PriceBookPayload, PriceUnit } from "@/hooks/api/use-campaign-requests";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { marketFlag, marketLabel, PRICE_CODE_LABELS, PRICE_UNIT_OPTIONS } from "../constants";
import { labelOf } from "../humanise";
import { isUnagreedRate } from "./rate-rows";
import { rateSchema, type RateFormValues } from "./rate-schema";

export interface RateEditRowProps {
	item: PriceBookItem;
	/** Whose rate this is, in words. Never an id — an account is a name to whoever reads this. */
	scopeLabel: string;
	/** How many cells sit between the market column and the actions column. */
	fieldColSpan: number;
	isSaving: boolean;
	onCancel: () => void;
	onSave: (payload: PriceBookPayload) => void;
}

/**
 * One rate, edited where it sits.
 *
 * Code and market are shown but not editable: the API upserts on (code, account, market), so
 * changing either of them here would not edit this rate, it would create a second one and leave
 * this one behind. Moving a rate is a delete and an add, and the table offers both.
 */
export function RateEditRow({
	item,
	scopeLabel,
	fieldColSpan,
	isSaving,
	onCancel,
	onSave,
}: RateEditRowProps): React.JSX.Element {
	const { control, handleSubmit } = useForm<RateFormValues>({
		resolver: zodResolver(rateSchema),
		defaultValues: {
			amount: item.amount,
			unit: item.unit,
			currency: item.currency || "SGD",
			note: item.note ?? "",
		},
	});
	// `FormControl` does not wire a label to its input the way `TextField` does, so without these
	// a screen reader announces each field as a bare "edit text".
	const fieldId = React.useId();

	const submit = handleSubmit((values) => {
		const note = values.note.trim();

		onSave({
			code: item.code,
			accountId: item.accountId,
			market: item.market,
			// String in, string out. Nothing here coerces to a number.
			amount: values.amount.trim(),
			unit: values.unit as PriceUnit,
			currency: values.currency,
			note: note.length > 0 ? note : null,
		});
	});

	return (
		<TableRow sx={{ bgcolor: "var(--mui-palette-background-level1)" }}>
			<TableCell>
				<Typography variant="body2" sx={{ fontWeight: 600 }}>
					{labelOf(PRICE_CODE_LABELS, item.code)}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					Editing {scopeLabel}
				</Typography>
			</TableCell>

			<TableCell>{item.market ? `${marketFlag(item.market)} ${marketLabel(item.market)}` : "All markets"}</TableCell>

			<TableCell colSpan={fieldColSpan}>
				<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ alignItems: "flex-start" }}>
					<Controller
						control={control}
						name="amount"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} size="small" sx={{ minWidth: 160 }}>
								<InputLabel htmlFor={`${fieldId}-amount`}>Amount</InputLabel>
								<OutlinedInput
									{...field}
									id={`${fieldId}-amount`}
									label="Amount"
									placeholder="1200.0000"
									inputProps={{ inputMode: "decimal" }}
								/>
								{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
							</FormControl>
						)}
					/>

					<Controller
						control={control}
						name="unit"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} size="small" sx={{ minWidth: 150 }}>
								<InputLabel id={`${fieldId}-unit`}>Unit</InputLabel>
								<Select {...field} labelId={`${fieldId}-unit`} label="Unit">
									{PRICE_UNIT_OPTIONS.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
								{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
							</FormControl>
						)}
					/>

					<Controller
						control={control}
						name="currency"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} size="small" sx={{ minWidth: 110 }}>
								<InputLabel htmlFor={`${fieldId}-currency`}>Currency</InputLabel>
								<OutlinedInput {...field} id={`${fieldId}-currency`} label="Currency" placeholder="SGD" />
								{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
							</FormControl>
						)}
					/>

					<Controller
						control={control}
						name="note"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} size="small" sx={{ flex: "1 1 auto", minWidth: 220 }}>
								<InputLabel htmlFor={`${fieldId}-note`}>Note</InputLabel>
								<OutlinedInput {...field} id={`${fieldId}-note`} label="Note" placeholder="Never shown to a client" />
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : isUnagreedRate(item.note) ? (
									<Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
										This note is what marks the rate unconfirmed. Replace it with what was agreed.
									</Typography>
								) : null}
							</FormControl>
						)}
					/>
				</Stack>
			</TableCell>

			<TableCell align="right">
				<Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
					<Button size="small" variant="outlined" onClick={onCancel} disabled={isSaving}>
						Cancel
					</Button>
					<Button size="small" variant="contained" onClick={submit} disabled={isSaving}>
						Save
					</Button>
				</Stack>
			</TableCell>
		</TableRow>
	);
}
